import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { orderBy } from 'lodash'

import { AnalyticsPerformance } from './utils/types'
import { AnalyticsField, DuckDBSchemaType, FilterField } from '@types'

import { ANALYTICS_CAPTIONS } from '../utils/const'
import { ASSET_FIELD } from '@/const'

import { DuckDBService } from '@/services/duckdb'

import { getCurrentDate } from '@/helpers/dates'
import { getFilterTags, getFilterValues } from '../utils/helpers'

import useAnalyticsStore from '../index'
import { useTagsBunchStore } from '@/store/tags/bunch'

export const TABLE_NAME = 'analytics_performance'

const TABLE_SCHEMA: Record<string, DuckDBSchemaType> = {
  [AnalyticsField.DATE]: 'DATE',
  [AnalyticsField.ACCOUNT_ID]: 'VARCHAR',
  [AnalyticsField.ASSET_ID]: 'VARCHAR',
  [AnalyticsField.TAGS]: 'VARCHAR[]',
  [AnalyticsField.VALUE_START]: 'DOUBLE',
  [AnalyticsField.VALUE_END]: 'DOUBLE',
  [AnalyticsField.CHANGE]: 'DOUBLE',
  [AnalyticsField.PERCENT_CHANGE]: 'DOUBLE',
  [AnalyticsField.CAPITAL_FLOW]: 'DOUBLE',
  [AnalyticsField.REALIZED_GAIN]: 'DOUBLE',
  [AnalyticsField.UNREALIZED_GAIN]: 'DOUBLE',
}

export default defineStore('analytics-performance', () => {
  // INIT
  const duckdbInstance = DuckDBService.instance()

  const loading = ref(false)
  const deprecated = ref<boolean>()
  const trigger = ref(0)

  const datesRange = ref<string[]>([])

  // GETTERS
  const isInit = computed(() => deprecated.value !== undefined)

  const getDatesRange = computed(() => datesRange.value)

  // SETTERS
  const setList = async (data: {
    performance_view: Record<string, AnalyticsPerformance[]>
  }) => {
    if (deprecated.value === false) return
    if (!data.performance_view) return
    datesRange.value = Object.keys(data.performance_view ?? {})
    deprecated.value = false
    await duckdbInstance.createTable(TABLE_NAME, TABLE_SCHEMA)
    await Promise.all(
      Object.entries(data.performance_view ?? {}).map(([date, performance]) =>
        duckdbInstance.pushData(
          TABLE_NAME,
          performance.map(item => ({ 
            ...item, 
            [AnalyticsField.DATE]: date
          })),
          TABLE_SCHEMA,
        ),
      ),
    )
    trigger.value++
  }

  // ACTIONS

  const clear = () => {
    datesRange.value = []
    loading.value = false
    deprecated.value = undefined
    trigger.value = 0
  }

  return {
    isLoading: loading,
    isDeprecated: deprecated,

    isInit,
    trigger,

    getDatesRange,

    setList,

    fetch,

    clear,
  }
})

type UseAnalyticsPerformanceFiltersOptions = {
  scoped?: boolean
  withDate?: boolean
  withAmounts?: boolean
  excludePercentChange?: boolean
}

export const useAnalyticsPerformanceFilters = ({
  scoped = false,
  withDate = false,
  withAmounts = false,
  excludePercentChange = false,
}: UseAnalyticsPerformanceFiltersOptions = {}) => {
  const analyticsStore = useAnalyticsStore()
  const tagsBunchStore = useTagsBunchStore()

  const analyticsDate = computed(() => analyticsStore.getDate || getCurrentDate({}))

  const getValues = getFilterValues(TABLE_NAME, analyticsDate, scoped, tagsBunchStore)
  const getTags = getFilterTags(TABLE_NAME, analyticsDate, tagsBunchStore, scoped)

  const filters = computed(() => {
    const result: FilterField[] = []
    if (withDate) {
      result.push({
        name: AnalyticsField.DATE,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.DATE],
        isDate: true,
      })
    }
    result.push(
      {
        name: AnalyticsField.ACCOUNT_NAME,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ACCOUNT],
        getter: (context, comparison) => getValues(AnalyticsField.ACCOUNT_ID, undefined, context, comparison)
      },
      {
        name: AnalyticsField.ACCOUNT_TYPE,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ACCOUNT_TYPE],
        getter: (context, comparison) => getValues(AnalyticsField.ACCOUNT_ID, ASSET_FIELD.TYPE, context, comparison)
      },
      {
        name: AnalyticsField.ASSET_NAME,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ASSET],
        getter: (context, comparison) => getValues(AnalyticsField.ASSET_ID, undefined, context, comparison)
      },
      {
        name: AnalyticsField.ASSET_TYPE,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ASSET_TYPE],
        getter: (context, comparison) => getValues(AnalyticsField.ASSET_ID, ASSET_FIELD.TYPE, context, comparison)
      },
    )
    if (withAmounts) {
      result.push(
        {
          name: AnalyticsField.VALUE_START,
          caption: ANALYTICS_CAPTIONS[AnalyticsField.VALUE_START],
          isAmount: true,
        },
        {
          name: AnalyticsField.CHANGE,
          caption: ANALYTICS_CAPTIONS[AnalyticsField.CHANGE],
          isAmount: true,
        },
        ...(excludePercentChange ? [] : [{
          name: AnalyticsField.PERCENT_CHANGE,
          caption: ANALYTICS_CAPTIONS[AnalyticsField.PERCENT_CHANGE],
          isAmount: true,
        }]),
        {
          name: AnalyticsField.CAPITAL_FLOW,
          caption: ANALYTICS_CAPTIONS[AnalyticsField.CAPITAL_FLOW],
          isAmount: true,
        },
        {
          name: AnalyticsField.REALIZED_GAIN,
          caption: ANALYTICS_CAPTIONS[AnalyticsField.REALIZED_GAIN],
          isAmount: true,
        },
        {
          name: AnalyticsField.UNREALIZED_GAIN,
          caption: ANALYTICS_CAPTIONS[AnalyticsField.UNREALIZED_GAIN],
          isAmount: true,
        },
        {
          name: AnalyticsField.VALUE_END,
          caption: ANALYTICS_CAPTIONS[AnalyticsField.VALUE_END],
          isAmount: true,
        },
      )
    }
    tagsBunchStore.getMetricTags?.forEach(name => {
      result.push({
        name,
        caption: name,
        getter: (context, comparison) => getTags(name, context, comparison)
      })
    })
    return orderBy(result, item => item.caption.replace('% ', ''))
  })

  return {
    filters,
  }
}
